/**
 * Um item de resultado da busca global (Cmd+K) — story E3.2.1 / E3.2.2.
 *
 * Renderiza ícone do tipo + título e snippet com os termos buscados destacados em
 * ``<mark>``. O destaque vem PRONTO do servidor (``ts_headline``, story E3.2.2):
 * o backend envolve cada match em sentinelas private-use (não-HTML) e aqui o
 * ``parseHighlight`` as converte em ``<mark>`` via React — texto escapado, sem
 * ``dangerouslySetInnerHTML``, XSS-safe por construção. Resultados aproximados
 * (fuzzy) vêm sem sentinelas → exibidos sem destaque.
 */
import React from "react";

import { CommandItem } from "@/components/ui/command";
import {
  ENTITY_TYPE_META,
  isSearchableEntityType,
  parseHighlight,
  stripHighlight,
} from "@/lib/globalSearch";

interface SearchResultItemProps {
  entityType: string;
  entityId: string;
  /** Título já marcado pelo servidor (pode conter sentinelas de highlight). */
  title: string | null;
  /** Snippet já marcado pelo servidor (pode conter sentinelas de highlight). */
  snippet: string | null;
  onSelect: () => void;
}

function Highlighted({ marked }: { marked: string }) {
  return (
    <>
      {parseHighlight(marked).map((seg, i) =>
        seg.match ? (
          <mark
            key={i}
            className="rounded-sm bg-yellow-200 px-0.5 text-foreground dark:bg-yellow-600/50"
          >
            {seg.text}
          </mark>
        ) : (
          <React.Fragment key={i}>{seg.text}</React.Fragment>
        )
      )}
    </>
  );
}

const SearchResultItem: React.FC<SearchResultItemProps> = ({
  entityType,
  entityId,
  title,
  snippet,
  onSelect,
}) => {
  const meta = isSearchableEntityType(entityType) ? ENTITY_TYPE_META[entityType] : null;
  const Icon = meta?.icon;
  // Vazio = nada além das sentinelas. Checa o texto SEM marcas (uma sentinela não é
  // whitespace, então `"".trim()` seria truthy e furaria o placeholder).
  // Mas renderiza o texto MARCADO (parseHighlight precisa das sentinelas).
  const hasTitle = !!title && stripHighlight(title).trim().length > 0;
  const hasSnippet = !!snippet && stripHighlight(snippet).trim().length > 0;

  return (
    <CommandItem
      value={`${entityType}:${entityId}`}
      onSelect={onSelect}
      className="flex items-start gap-3"
    >
      {Icon && <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />}
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">
          {hasTitle ? <Highlighted marked={title as string} /> : "(sem título)"}
        </div>
        {hasSnippet && (
          <div className="line-clamp-2 text-xs text-muted-foreground">
            <Highlighted marked={snippet as string} />
          </div>
        )}
      </div>
    </CommandItem>
  );
};

export default SearchResultItem;
